import SharedPreferences from "../../../constants/SharedPreferences";
import {
  saveInStorage,
  getFromStorage,
  generateHMacHash,
  getEncryptionCurveFromStorage,
  getErrorMessage,
  logApiResponse,
} from "../../../util/Utility";
import { getNonAuthRestClientForAdmin } from "../../RestUtility";
import { aeadDecryptWithPasscode } from "../../../crypto/AuthenticatedEncryption";
import BayunError from "../../../constants/BayunError";
import { AuthenticateResponse } from "../../../BayunCore";
import { decryptAsymmetric } from "../../../util/AsymmetricEncryption";
import {
  getMessageFromSignatureString,
  retrieveAndverifyLastSignature,
  verifyMessageForPublicKeyTag,
  verifyAndPersistOrgSettingsFromLastUpdatedAt,
} from "../../../util/SignatureUtil";
import { derivePbkdf2Encoded, encodeKey } from "../../../crypto/PBKDF2";
import { hmacSha256Raw, asRawKey } from "../../../crypto/KeyMaterial";
import SigningPublicKeyTags from "../../../constants/SigningPublicKeyTags";
import { verifyLockBoxResponse } from "./lockBoxVerification";
import { prepareLockBoxVerificationKeys } from "../memberApi";
/**
 * Verifies one server-signed identity field and returns its raw value.
 *
 * Every failure throws. Reporting through failureCallback alone let the caller continue on an
 * identity binding that did not verify: verifyAuthReponse returned normally and authApi returned
 * the response as a success, so a tampered orgId / userId / memberAppId was accepted.
 */
async function requireVerifiedSignedValue(
  sessionId,
  signedValue,
  fieldName,
  failureCallback,
) {
  if (!(await retrieveAndverifyLastSignature(sessionId, signedValue))) {
    console.log(`${fieldName} verification failed.`);
    failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
    throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
  }
  return getMessageFromSignatureString(signedValue);
}

export async function verifyAuthReponse(sessionId, authResponse, failureCallback) {
  // verify adminPublicKey
  if (authResponse.data.adminPublicKey) {
    await saveInStorage(
      sessionId,
      SharedPreferences.ADMIN_PUBLIC_KEY,
      await requireVerifiedSignedValue(
        sessionId,
        authResponse.data.adminPublicKey,
        "adminPublicKey",
        failureCallback,
      ),
    );
  }

  if (authResponse.data.memberId) {
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_ID,
      await requireVerifiedSignedValue(
        sessionId,
        authResponse.data.memberId,
        "memberId",
        failureCallback,
      ),
    );
  }

  if (authResponse.data.orgId) {
    //Verify and save orgId
    await saveInStorage(
      sessionId,
      SharedPreferences.ORG_ID,
      await requireVerifiedSignedValue(
        sessionId,
        authResponse.data.orgId,
        "orgId",
        failureCallback,
      ),
    );
  }

  // verify and save userId
  if (authResponse.data.userId) {
    await saveInStorage(
      sessionId,
      SharedPreferences.USER_ID,
      await requireVerifiedSignedValue(
        sessionId,
        authResponse.data.userId,
        "userId",
        failureCallback,
      ),
    );
  }

  for (let memberApp of authResponse.data.memberApps) {
    if (memberApp.memberAppId) {
      //Verify and save memberAppId review
      const memberAppId = await requireVerifiedSignedValue(
        sessionId,
        memberApp.memberAppId,
        "memberAppId",
        failureCallback,
      );
      await saveInStorage(sessionId, SharedPreferences.APP_ID, memberApp.appId);
      //review, currently saving the last memberAppId
      if (await getFromStorage(sessionId, SharedPreferences.USER_ID)) {
        await saveInStorage(
          sessionId,
          SharedPreferences.MEMBER_APP_ID,
          memberAppId,
        );
      }
    }
  }
}

export async function loginViaValidatePasscode(
  sessionId,
  memberAppId,
  passcode,
  successCallback,
  failureCallback,
  securityQuestionsCallback,
  passphraseCallback,
) {
  // create a rest client
  const restClient = getNonAuthRestClientForAdmin(sessionId);
  const orgName = await getFromStorage(sessionId, SharedPreferences.ORG_NAME);

  const orgMemberId = await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  );

  try {
    // create the authPasscodeHash

    const authPasscodeHash = await derivePbkdf2Encoded(
      passcode,
      await getFromStorage(sessionId, SharedPreferences.MEMBER_AUTH_SALT), // check is it saved or not
    );

    const userId = await getFromStorage(sessionId, SharedPreferences.USER_ID);

    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_ID,
      memberAppId,
    );

    let validatePasscodeResponse;
    try {
      validatePasscodeResponse = await restClient.post(
        "admin/validatePasscode",
        {
          authPasscodeHash,
          memberAppId,
          userId,
        },
      );
    } catch (error) {
      console.log("Bayun Error : ", error);

      if (error != undefined) {
        if (error.response != undefined) {
          failureCallback(error?.response?.data ?? error);
        } else {
          //throw error;
          failureCallback(error);
        }
      }
      return;
    }
    // console.log(/validatePasscodeResponse);
    await logApiResponse("validatePasscodeResponse", validatePasscodeResponse);

    await verifyResponse(sessionId, validatePasscodeResponse, failureCallback);

    // decrypt the memberApp private key with passcode
    const decryptedMemberAppPrivateKey = await aeadDecryptWithPasscode(
      validatePasscodeResponse.data.memberAppPrivateKey,
      passcode,
      await getFromStorage(sessionId, SharedPreferences.MEMBER_KEY_SALT),
      orgMemberId,
      sessionId,
    );

    if (!decryptedMemberAppPrivateKey) {
      console.log("MemberAppPrivateKey decryption failed.");
      failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_APP_PRIVATE_KEY,
      decryptedMemberAppPrivateKey,
    );

    const lockboxResp = validatePasscodeResponse.data.memberLockBox;

    let memberPrivateKey;
    const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);

    if (decryptedMemberAppPrivateKey && lockboxResp.memberPrivateKeyInner_kek) {
      memberPrivateKey = await decryptAsymmetric(
        lockboxResp.memberPrivateKey,
        decryptedMemberAppPrivateKey,
        lockboxResp.memberPrivateKeyInner_kek,
        orgMemberId,
        encryptionCurve,
      );
    }

    if (!memberPrivateKey) {
      console.log("MemberPrivateKey decryption failed.");
      failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    if (
      lockboxResp.memberStatus == "Cancelled" ||
      lockboxResp.memberStatus == "Registered"
    ) {
      throw BayunError.ERROR_USER_INACTIVE;
    }

    // Persist the signature verification keys before any verification runs; otherwise every
    // *SigningPublicKeyTag lookup resolves to "" and silently fails.
    await prepareLockBoxVerificationKeys(sessionId, lockboxResp);

    await verifyLockBoxResponse(sessionId, lockboxResp, memberPrivateKey);

    // Org settings verification reads ADMIN_SIGNING_PUBLIC_KEY from storage and throws when it is
    // absent, so it must run after prepareLockBoxVerificationKeys. Verifying earlier made a first
    // login on a fresh session fail outright, or verify against a stale key left by a prior
    // session. The memberApi lockbox path already used this order.
    if (lockboxResp.signedSettings && lockboxResp.orgSettingsLastUpdatedAt != null) {
      const adminPublicKey = await getFromStorage(
        sessionId,
        SharedPreferences.ADMIN_PUBLIC_KEY,
      );

      // No `if (adminPublicKey)` gate: skipping verification when the key is absent is exactly
      // the downgrade an attacker wants, so let the verifier fail closed instead.
      await verifyAndPersistOrgSettingsFromLastUpdatedAt(
        lockboxResp.signedSettings,
        lockboxResp.orgSettingsLastUpdatedAt,
        sessionId,
        adminPublicKey,
        orgName,
        {
          keyValidationPolicy: lockboxResp.keyValidationPolicy,
          memberApproval: lockboxResp.memberApproval,
          appApproval: lockboxResp.appApproval,
        },
      );
    }

    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_PRIVATE_KEY,
      memberPrivateKey,
    );

    // decrypt memberKey and save it
    const memberKey = asRawKey(
      await decryptAsymmetric(
        lockboxResp.memberKey,
        memberPrivateKey,
        lockboxResp.memberKey_kek,
        orgMemberId,
        encryptionCurve,
      ),
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_KEY,
      encodeKey(memberKey),
    );

    // decrypt the memberSigningPrivateKey and save it
    const memberSigningPrivateKey = await decryptAsymmetric(
      lockboxResp.memberSigningPrivateKey,
      memberPrivateKey,
      lockboxResp.memberSigningPrivateKey_kek,
      orgMemberId,
      encryptionCurve,
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY,
      memberSigningPrivateKey,
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.MEMBER_SIGNING_PUBLIC_KEY,
      getMessageFromSignatureString(lockboxResp.memberSigningPublicKey),
    );

    // todo : for now targetForLockBoxTransfer is wraped inside successCallback as null, need to handle it later
    if (lockboxResp.orgKey && lockboxResp.orgKey_kek) {
      // decrypt orgkey and save it
      const orgkey = asRawKey(
        await decryptAsymmetric(
          lockboxResp.orgKey,
          memberPrivateKey,
          lockboxResp.orgKey_kek,
          orgMemberId,
          encryptionCurve,
        ),
      );

      if (!orgkey) {
        console.log("orgkey decryption failed.");
        failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }

      await saveInStorage(
        sessionId,
        SharedPreferences.ORG_KEY,
        encodeKey(orgkey),
      );

      const lmsTransectionKey = await hmacSha256Raw(orgkey, "lms" + orgName);

      const request = {
        memberAppId: memberAppId,
        lmsTransactionKey: encodeKey(lmsTransectionKey),
      };

      const targetForLockBoxTransferResponse = await restClient.post(
        "/admin/targetForLockBoxTransfer",
        request,
      );
      // console.log(changePasscodeResponse);
      await logApiResponse(
        "targetForLockBoxTransferResponse",
        targetForLockBoxTransferResponse,
      );
    }
    successCallback({
      sessionId,
      targetForLockBoxTransfer: null,
      authenticationResponse: AuthenticateResponse.SUCCESS,
    });
  } catch (e) {
    // Swallowing this left the caller with no success and no failure callback, so the UI hung
    // silently on any verification error. Surface it instead.
    console.error(e);
    failureCallback(getErrorMessage(sessionId, e));
    throw e;
  }
}

export async function verifyResponse(sessionId, response, failureCallback) {
  // if (response.data.adminPublicKey) {
  //   if (
  //     !(await retrieveAndverifyLastSignature(
  //       sessionId,
  //       response.data.adminPublicKey,
  //     ))
  //   ) {
  //     console.log("adminPublicKey verification failed.");
  //     failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
  //   } else {
  //     await saveInStorage(
  //       sessionId,
  //       SharedPreferences.ADMIN_PUBLIC_KEY,
  //       getMessageFromSignatureString(response.data.adminPublicKey),
  //     );
  //   }
  // }

  // verify ghostMemberId
  if (response.data.ghostMemberId) {
    if (
      !(await verifyMessageForPublicKeyTag(
        sessionId,
        response.data.ghostMemberId,
        "",
        SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG,
      ))
    ) {
      console.log("ghostMemberId verification failed.");
      failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      // Throw rather than return: a bare return leaves the caller believing verification
      // succeeded and it proceeds to decrypt the lockbox.
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    } else {
      await saveInStorage(
        sessionId,
        SharedPreferences.GHOST_MEMBER_ID,
        response.data.ghostMemberId,
      );
    }
  }

  // verify ghostMemberPublicKey
  if (response.data.ghostMemberPublicKey) {
    if (
      !(await verifyMessageForPublicKeyTag(
        sessionId,
        response.data.ghostMemberPublicKey,
        "",
        SigningPublicKeyTags.BAYUN_SERVER_PUBLIC_KEY_TAG,
      ))
    ) {
      console.log("ghostMemberPublicKey verification failed.");
      failureCallback(BayunError.ERROR_MEMBER_VERIFICATION_FAILED);
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    } else {
      await saveInStorage(
        sessionId,
        SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY,
        getMessageFromSignatureString(response.data.ghostMemberPublicKey),
      );
      await saveInStorage(
        sessionId,
        SharedPreferences.GHOST_MEMBER_SIGNING_PUBLIC_KEY_WITH_SIGNATURES,
        response.data.ghostMemberPublicKey,
      );
    }
  }
}
